/*
 * pp-direcciones.js — la libreta de direcciones del cliente, dentro del portal.
 *
 * POR QUÉ EXISTE
 * «Si le pico a direcciones, me manda al viejo. […] Pésimo. No podemos estar
 * haciendo esto» (Padrino, 24-sep-2026). Bajo cuentas nuevas Shopify intercepta
 * /account/* con un 302 a su propia pantalla, y el form del tema no puede
 * escribir. El backend es _shared/storefront/direcciones.ts, con su candado de
 * pertenencia.
 *
 * MEDIDO ANTES DE ESCRIBIR ESTO (26-sep-2026):
 *   · 19,214 clientes tienen MÁS DE UNA dirección; 3,236 tienen cinco o más y
 *     el máximo real es 228. La lista no puede asumir «dos o tres».
 *   · 10,474 direcciones sin calle y 22,927 sin código postal. Por eso la
 *     página SEÑALA lo incompleto antes de listar: en una lista plana el hueco
 *     no se nota, y el CP es la llave con la que las estadísticas del portal
 *     agrupan por sucursal.
 *
 * Vanilla, sin dependencias. El formulario es un <dialog> nativo.
 */
(function () {
  'use strict';

  // El PROTOCOLO (endpoint, campos, validación, cuerpo, lectura de errores)
  // vive en assets/pp-direcciones-nucleo.js, el mismo que usa Paco 3.0. Aquí
  // se pinta y se traduce: el núcleo devuelve claves data-t-*.
  var N = window.PPDireccionesNucleo;

  var root = document.querySelector('[data-pp-dirs]');
  if (!root) return;
  if (window.Shopify && window.Shopify.designMode) return;
  if (!N) return;

  var loadingEl = root.querySelector('[data-pp-dirs-loading]');
  var listaEl = root.querySelector('[data-pp-dirs-lista]');
  var vacioEl = root.querySelector('[data-pp-dirs-vacio]');
  var errorEl = root.querySelector('[data-pp-dirs-error]');
  var avisoEl = root.querySelector('[data-pp-dirs-aviso]');
  var accionesEl = root.querySelector('[data-pp-dirs-acciones]');
  var agregarEl = root.querySelector('[data-pp-dirs-agregar]');
  // Las direcciones de la EMPRESA (22-oct-2026) y el encabezado que separa las
  // personales. Ver pintarEmpresas.
  var empresaEl = root.querySelector('[data-pp-dirs-empresa]');
  var personalesHeadEl = root.querySelector('[data-pp-dirs-personales-head]');

  function t(k) { return root.getAttribute('data-t-' + k) || ''; }

  function el(tag, clase, texto) {
    var n = document.createElement(tag);
    if (clase) n.className = clase;
    if (texto != null) n.textContent = texto;
    return n;
  }

  var SVG_NS = 'http://www.w3.org/2000/svg';
  function svgEquis() {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('aria-hidden', 'true');
    ['M18 6 6 18', 'M6 6l12 12'].forEach(function (d) {
      var path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', d);
      svg.appendChild(path);
    });
    return svg;
  }

  // Plural resuelto por Liquid (one/other ya interpolados con count 1 y 2) y el
  // dígito sustituido por el conteo real — mismo truco que el resto del portal,
  // para no reimplementar reglas de pluralización en JS.
  function plural(llaveUno, llaveOtros, n) {
    var tmpl = n === 1 ? t(llaveUno) : t(llaveOtros);
    return tmpl.replace(/\d+/, String(n));
  }

  // `listas`: la libreta personal ya llegó. Las dos lecturas corren en paralelo
  // y la de la empresa puede llegar primero; sin esta bandera, re-pintar la
  // libreta antes de tiempo enseñaría «no tienes direcciones» a quien sí tiene.
  var estado = { direcciones: [], tope: 0, listas: false, empresas: [] };

  // Los renglones de una dirección (calle, ciudad/estado/CP, teléfono). Los
  // usan las dos tarjetas, la personal y la de la empresa.
  function pintarRenglones(d) {
    var dir = el('p', 'pp-dirs__card-dir');
    var lineas = [
      [d.address1, d.address2].filter(Boolean).join(', '),
      [d.city, d.province, d.zip].filter(Boolean).join(', '),
      d.phone || ''
    ].filter(function (l) { return l; });
    lineas.forEach(function (l, i) {
      if (i) dir.appendChild(document.createElement('br'));
      dir.appendChild(document.createTextNode(l));
    });
    return dir;
  }

  // ── Qué le falta a una dirección ──────────────────────────────────────────
  // Sin calle no se puede entregar; sin CP no se puede cotizar el envío NI
  // agrupar por sucursal. Son los dos huecos que de verdad cuestan.
  function faltantes(d) {
    return N.faltantes(d).map(function (k) { return t(k); });
  }

  function mostrar(cual) {
    if (loadingEl) loadingEl.hidden = cual !== 'cargando';
    if (listaEl) listaEl.hidden = cual !== 'lista';
    if (vacioEl) vacioEl.hidden = cual !== 'vacio';
    if (errorEl) errorEl.hidden = cual !== 'error';
    // El botón de agregar vive con la lista Y con el vacío: si solo apareciera
    // con la lista, un cliente sin direcciones no tendría cómo crear la primera.
    if (accionesEl) accionesEl.hidden = !(cual === 'lista' || cual === 'vacio');
  }

  // ── Una tarjeta ──────────────────────────────────────────────────────────
  function pintarTarjeta(d) {
    var card = el('article', 'pp-dirs__card');
    if (d.is_default) card.classList.add('is-default');
    var falta = faltantes(d);
    if (falta.length) card.classList.add('is-incompleta');

    var head = el('div', 'pp-dirs__card-head');
    var nombre = [d.first_name, d.last_name].filter(Boolean).join(' ');
    head.appendChild(el('h3', 'pp-dirs__card-nombre', nombre || d.company || '—'));

    // La etiqueta de predeterminada es TEXTO, no un color: es la que el
    // checkout usa por default y confundirla cuesta un envío.
    if (d.is_default) head.appendChild(el('span', 'pp-dirs__badge', t('predeterminada')));
    card.appendChild(head);

    if (d.company && nombre) card.appendChild(el('p', 'pp-dirs__card-empresa', d.company));

    card.appendChild(pintarRenglones(d));

    if (falta.length) {
      var nota = el('p', 'pp-dirs__card-falta');
      nota.textContent = falta.join(' · ');
      card.appendChild(nota);
    }

    // ⚠️ LOS TRES LLEVAN `.pp-section__cta` ADEMÁS DEL MODIFICADOR.
    // `--ghost` sólo cambia fondo, color y borde: sin la clase base no hay
    // padding, ni forma de píldora, ni tipografía. La primera versión usó
    // sólo el modificador y salieron como rectángulos planos (visto en
    // pantalla el 28-sep-2026). Mismo bug que el `.pp-btn` del #782, con
    // otra cara: ahí la clase no cargaba, aquí cargaba a medias.
    var acc = el('div', 'pp-dirs__card-acciones');

    var editar = el('button', 'pp-section__cta pp-section__cta--ghost pp-dirs__btn', t('editar'));
    editar.type = 'button';
    editar.addEventListener('click', function () { abrirForm(d); });
    acc.appendChild(editar);

    if (!d.is_default) {
      var def = el('button', 'pp-section__cta pp-section__cta--ghost pp-dirs__btn', t('hacer-predeterminada'));
      def.type = 'button';
      def.addEventListener('click', function () { accion({ action: 'set_default', id: d.id }, t('ahora-default'), def); });
      acc.appendChild(def);
    }

    // «Usar para la empresa»: lleva ESTA dirección personal a la ubicación de
    // la empresa — nunca al revés, para que nadie se lleve las de la empresa
    // cuando deja de trabajar ahí. Sólo al admin y sólo con UNA ubicación
    // editable; con varias, cada una se cambia desde su propia tarjeta.
    var destino = ubicacionEditable();
    if (destino) {
      var usar = el('button', 'pp-section__cta pp-section__cta--ghost pp-dirs__btn', t('usar-para-empresa'));
      usar.type = 'button';
      usar.addEventListener('click', function () { abrirForm(d, { locationId: destino.id }); });
      acc.appendChild(usar);
    }

    // El botón de borrar NO se pinta en la predeterminada. Shopify lo rechaza
    // (deja el checkout sin a dónde mandar) y ofrecer un clic que va a fallar
    // es peor que no ofrecerlo: en su lugar se dice POR QUÉ.
    if (d.is_default) {
      acc.appendChild(el('span', 'pp-dirs__nota-default', t('no-borrar-default')));
    } else {
      var borrar = el('button', 'pp-section__cta pp-section__cta--ghost pp-dirs__btn pp-dirs__btn--borrar', t('borrar'));
      borrar.type = 'button';
      borrar.addEventListener('click', function () {
        // Confirmación EN LA TARJETA, no un alert(): un diálogo del navegador
        // bloquea la página y en móvil se ve como un error del sitio.
        if (borrar.getAttribute('data-confirmando') === '1') {
          accion({ action: 'delete', id: d.id }, t('borrada'), borrar);
          return;
        }
        borrar.setAttribute('data-confirmando', '1');
        borrar.textContent = t('si-borrar');
        borrar.classList.add('is-confirmando');
        setTimeout(function () {
          if (!borrar.isConnected) return;
          borrar.removeAttribute('data-confirmando');
          borrar.textContent = t('borrar');
          borrar.classList.remove('is-confirmando');
        }, 4000);
      });
      acc.appendChild(borrar);
    }

    card.appendChild(acc);
    return card;
  }

  function pintar() {
    if (!listaEl) return;
    listaEl.textContent = '';

    if (!estado.direcciones.length) {
      if (avisoEl) avisoEl.hidden = true;
      mostrar('vacio');
      return;
    }

    var incompletas = estado.direcciones.filter(function (d) { return faltantes(d).length; }).length;
    if (avisoEl) {
      if (incompletas) {
        avisoEl.textContent = plural('incompletas-one', 'incompletas-other', incompletas) + ' ' + t('completar');
        avisoEl.hidden = false;
      } else {
        avisoEl.hidden = true;
      }
    }

    estado.direcciones.forEach(function (d) { listaEl.appendChild(pintarTarjeta(d)); });
    mostrar('lista');

    // Con el tope alcanzado el botón de agregar se apaga con su razón: dejarlo
    // vivo prometería un guardado que el backend va a rechazar.
    if (agregarEl && estado.tope && estado.direcciones.length >= estado.tope) {
      agregarEl.disabled = true;
      agregarEl.title = t('tope');
    } else if (agregarEl) {
      agregarEl.disabled = false;
      agregarEl.removeAttribute('title');
    }
  }

  // ── Las direcciones de la EMPRESA ──────────────────────────────────────────
  // 22-oct-2026, Latam Brands DAI: cuando el cliente compra a nombre de su
  // empresa, el carrito NO le ofrece esta libreta: envía a la dirección de la
  // UBICACIÓN de la empresa. El cliente veía aquí Talisman 184 y en el carrito
  // Recursos Petroleros, que no estaba en ningún lado: «deberían aparecer las
  // dos en los dos lugares, ¿no?». Ahora se ven aquí, ARRIBA y separadas, con
  // la etiqueta de que esa es la que usa el carrito.
  //
  // Se piden EN VIVO al backend y no se guardan en ningún lado: si a la persona
  // la quitan de la empresa, la sección desaparece sola. Editar es del ADMIN de
  // la empresa (cambia la dirección de todos los que compran por ella); a los
  // demás se les dice por qué no pueden.

  // La única ubicación que este cliente puede editar, o null (ninguna o varias).
  function ubicacionEditable() {
    var editables = [];
    estado.empresas.forEach(function (e) {
      e.ubicaciones.forEach(function (u) { if (u.puedo_editar) editables.push(u); });
    });
    return editables.length === 1 ? editables[0] : null;
  }

  function pintarTarjetaEmpresa(u) {
    var card = el('article', 'pp-dirs__card pp-dirs__card--empresa');

    var head = el('div', 'pp-dirs__card-head');
    head.appendChild(el('h3', 'pp-dirs__card-nombre', u.nombre || '—'));
    // Con TEXTO, como la predeterminada: es la que el carrito va a usar.
    head.appendChild(el('span', 'pp-dirs__badge pp-dirs__badge--empresa', t('empresa-badge')));
    card.appendChild(head);

    var d = u.direccion;
    if (d) {
      var persona = [d.first_name, d.last_name].filter(Boolean).join(' ');
      if (persona) card.appendChild(el('p', 'pp-dirs__card-empresa', persona));
      if (d.company) card.appendChild(el('p', 'pp-dirs__card-empresa', d.company));
      card.appendChild(pintarRenglones(d));
      var falta = faltantes(d);
      if (falta.length) {
        card.classList.add('is-incompleta');
        card.appendChild(el('p', 'pp-dirs__card-falta', falta.join(' · ')));
      }
    } else {
      card.classList.add('is-incompleta');
      card.appendChild(el('p', 'pp-dirs__card-falta', t('empresa-sin-direccion')));
    }

    if (u.editable_en_checkout) card.appendChild(el('p', 'pp-dirs__nota-default', t('empresa-otra-en-checkout')));

    var acc = el('div', 'pp-dirs__card-acciones');
    if (u.puedo_editar) {
      var editar = el('button', 'pp-section__cta pp-section__cta--ghost pp-dirs__btn', t('editar'));
      editar.type = 'button';
      editar.addEventListener('click', function () { abrirForm(d, { locationId: u.id }); });
      acc.appendChild(editar);
    } else {
      acc.appendChild(el('span', 'pp-dirs__nota-default', t('empresa-solo-admin')));
    }
    card.appendChild(acc);
    return card;
  }

  function pintarEmpresas() {
    if (!empresaEl) return;
    empresaEl.textContent = '';
    var hay = estado.empresas.length > 0;
    empresaEl.hidden = !hay;
    if (personalesHeadEl) personalesHeadEl.hidden = !hay;
    // Con la sección de la empresa en pantalla, «Agregar dirección» a secas
    // deja la duda de a cuál de las dos libretas agrega (lo preguntó Jaime al
    // verlo en el banco, 22-oct). Agrega SIEMPRE a la personal: lo dice.
    if (agregarEl) {
      var etiqueta = agregarEl.querySelector('[data-pp-dirs-agregar-texto]');
      if (etiqueta) etiqueta.textContent = hay ? t('agregar-personal') : t('agregar');
    }
    estado.empresas.forEach(function (e) {
      var head = el('div', 'pp-dirs__grupo-head');
      head.appendChild(el('h2', 'pp-dirs__grupo-titulo', t('empresa-titulo').replace('__EMPRESA__', e.nombre || '')));
      head.appendChild(el('p', 'pp-dirs__grupo-sub', t('empresa-sub')));
      empresaEl.appendChild(head);
      var lista = el('div', 'pp-dirs__lista');
      e.ubicaciones.forEach(function (u) { lista.appendChild(pintarTarjetaEmpresa(u)); });
      empresaEl.appendChild(lista);
    });
  }

  // Si falla, la sección CALLA (no afirma que no hay empresa) y la libreta
  // personal sigue funcionando igual que antes.
  function cargarEmpresa() {
    return N.listarEmpresa().then(function (r) {
      estado.empresas = r.estado === 'ok'
        ? r.empresas.filter(function (e) { return e && e.ubicaciones && e.ubicaciones.length; })
        : [];
      pintarEmpresas();
      // Re-pinta la libreta para que salga (o se quite) «Usar para la empresa».
      if (estado.listas) pintar();
    });
  }

  // ── El formulario ────────────────────────────────────────────────────────
  var CAMPOS = N.CAMPOS;

  var dialogo = null;

  function construirDialogo() {
    var dlg = document.createElement('dialog');
    dlg.className = 'pp-dirs__dialog';
    dlg.setAttribute('aria-labelledby', 'pp-dirs-form-titulo');

    var form = el('form', 'pp-dirs__form');
    form.method = 'dialog';
    form.noValidate = true;

    var head = el('div', 'pp-dirs__form-head');
    head.appendChild(el('h2', 'pp-dirs__form-titulo', ''));
    var cerrar = el('button', 'pp-dirs__form-cerrar', '');
    cerrar.type = 'button';
    cerrar.setAttribute('aria-label', t('cerrar'));
    // El SVG se arma con DOM en vez de innerHTML. Aquí el contenido es un
    // literal y no hay dato de nadie, pero un innerHTML en un archivo que
    // también pinta direcciones del cliente es el patrón que después alguien
    // copia con una variable adentro.
    cerrar.appendChild(svgEquis());
    cerrar.addEventListener('click', function () { dlg.close(); });
    head.appendChild(cerrar);
    form.appendChild(head);

    // Sólo al editar la dirección de la EMPRESA: avisa que el cambio es para
    // todos los que compran a nombre de ella, no sólo para quien la edita.
    var nota = el('p', 'pp-dirs__form-nota', '');
    nota.hidden = true;
    form.appendChild(nota);

    var grid = el('div', 'pp-dirs__form-grid');
    CAMPOS.forEach(function (c) {
      var campo = el('label', 'pp-dirs__campo' + (c.k === 'address1' ? ' pp-dirs__campo--ancho' : ''));
      var etiqueta = el('span', 'pp-dirs__campo-label', t(c.l) + (c.req ? '' : ' ' + t('opcional')));
      campo.appendChild(etiqueta);
      var input = document.createElement('input');
      input.type = c.k === 'phone' ? 'tel' : 'text';
      input.className = 'pp-dirs__input';
      input.name = c.k;
      if (c.req) input.required = true;
      if (c.cp) {
        // Teclado numérico en móvil. SIN tope de 5 ni `pattern`: pegar «64 000»
        // o «C.P. 64000» se recortaba y se rechazaba (12-oct-2026). El núcleo
        // se queda con los dígitos (`soloCp`) y él dice si son 5.
        input.inputMode = 'numeric';
        input.autocomplete = 'postal-code';
        input.maxLength = 12;
      }
      campo.appendChild(input);
      campo.appendChild(el('span', 'pp-dirs__campo-error', ''));
      if (c.cp) campo.appendChild(el('span', 'pp-dirs__campo-ayuda', t('cp-ayuda')));
      grid.appendChild(campo);
    });
    form.appendChild(grid);

    form.appendChild(el('p', 'pp-dirs__form-error', ''));

    var pie = el('div', 'pp-dirs__form-pie');
    var cancelar = el('button', 'pp-section__cta pp-section__cta--ghost', t('cancelar'));
    cancelar.type = 'button';
    cancelar.addEventListener('click', function () { dlg.close(); });
    var guardar = el('button', 'pp-section__cta', t('guardar'));
    guardar.type = 'submit';
    pie.appendChild(cancelar);
    pie.appendChild(guardar);
    form.appendChild(pie);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      enviarForm(dlg, form, guardar);
    });

    dlg.appendChild(form);
    document.body.appendChild(dlg);
    return dlg;
  }

  // `empresa` = { locationId }: el mismo formulario guarda la dirección de envío
  // de esa ubicación de la empresa. `d` son los valores con que se abre (la
  // dirección actual de la ubicación, o la personal que se quiere «usar para la
  // empresa»); en ese modo NUNCA se toca la dirección personal de donde vino.
  function abrirForm(d, empresa) {
    if (!dialogo) dialogo = construirDialogo();
    var form = dialogo.querySelector('form');
    form.querySelector('.pp-dirs__form-titulo').textContent = empresa
      ? t('empresa-editar-titulo')
      : (d ? t('titulo-editar') : t('nueva'));
    form.querySelector('.pp-dirs__form-error').textContent = '';
    var nota = form.querySelector('.pp-dirs__form-nota');
    nota.textContent = empresa ? t('empresa-aviso-form') : '';
    nota.hidden = !empresa;
    form.setAttribute('data-id', !empresa && d ? String(d.id) : '');
    form.setAttribute('data-location', empresa ? String(empresa.locationId) : '');

    CAMPOS.forEach(function (c) {
      var input = form.querySelector('[name="' + c.k + '"]');
      input.value = d && d[c.k] != null ? String(d[c.k]) : '';
      input.closest('.pp-dirs__campo').querySelector('.pp-dirs__campo-error').textContent = '';
      input.setAttribute('aria-invalid', 'false');
    });

    dialogo.showModal();
    var primero = form.querySelector('[name="address1"]');
    if (primero) primero.focus();
  }

  // Validación en el cliente que ESPEJA la del servidor, no la sustituye: el
  // backend valida igual porque nadie puede confiar en el navegador. Aquí está
  // para que el cliente no tenga que esperar un viaje para saber qué le falta.
  function validar(form) {
    var valores = {};
    CAMPOS.forEach(function (c) { valores[c.k] = form.querySelector('[name="' + c.k + '"]').value; });
    var errores = N.validar(valores);
    var cuantos = 0;
    CAMPOS.forEach(function (c) {
      var input = form.querySelector('[name="' + c.k + '"]');
      var slot = input.closest('.pp-dirs__campo').querySelector('.pp-dirs__campo-error');
      var msg = errores[c.k] ? t(errores[c.k]) : '';
      slot.textContent = msg;
      input.setAttribute('aria-invalid', msg ? 'true' : 'false');
      if (msg) {
        cuantos++;
        if (cuantos === 1) input.focus();
      }
    });
    return cuantos === 0;
  }


  function enviarForm(dlg, form, boton) {
    if (!validar(form)) return;
    var id = form.getAttribute('data-id');
    var ubicacion = form.getAttribute('data-location');
    var valores = {};
    CAMPOS.forEach(function (c) { valores[c.k] = form.querySelector('[name="' + c.k + '"]').value; });
    var cuerpo = ubicacion ? N.cuerpoEmpresa(ubicacion, valores) : N.cuerpo(id, valores);

    boton.disabled = true;
    pedir(cuerpo).then(function (r) {
      if (r.ok) {
        dlg.close();
        if (ubicacion) {
          // El aviso va DESPUÉS de re-leer: así lo que dice («el carrito ya
          // envía aquí») coincide con la tarjeta que se ve.
          cargarEmpresa().then(function () { avisar(t('empresa-guardada')); });
          return;
        }
        avisar(t('guardada'));
        cargar();
        return;
      }
      // El backend dice QUÉ campo falla. Se pinta junto a ese campo, no en un
      // «revisa los datos» que obliga a adivinar.
      var slot = r.field ? form.querySelector('[name="' + r.field + '"]') : null;
      if (slot) {
        var campo = slot.closest('.pp-dirs__campo');
        campo.querySelector('.pp-dirs__campo-error').textContent = r.mensaje || t('requerido');
        slot.setAttribute('aria-invalid', 'true');
        slot.focus();
      } else {
        form.querySelector('.pp-dirs__form-error').textContent = r.mensaje || t('error-guardar');
      }
    }).catch(function () {
      form.querySelector('.pp-dirs__form-error').textContent = t('error-guardar');
    }).then(function () { boton.disabled = false; });
  }

  // ── Acciones de una tarjeta ──────────────────────────────────────────────
  function accion(cuerpo, mensajeOk, boton) {
    if (boton) boton.disabled = true;
    pedir(cuerpo).then(function (r) {
      if (r.ok) { avisar(mensajeOk); cargar(); return; }
      avisar(r.mensaje || t('error'), true);
      if (boton) boton.disabled = false;
    }).catch(function () {
      avisar(t('error'), true);
      if (boton) boton.disabled = false;
    });
  }

  function pedir(cuerpo) {
    return N.pedir(cuerpo).then(function (r) {
      if (r.ok) return r;
      // La clave del núcleo se traduce con los textos de la página ('sesion'
      // no aplica aquí: la página ya exige cuenta).
      return { ok: false, field: r.field, mensaje: t(r.clave === 'sesion' ? 'error' : r.clave) };
    });
  }

  // Un aviso que se lee, y que un lector de pantalla anuncia: el <div> del
  // shell ya trae role="status".
  var avisoTimer = null;
  function avisar(texto, esError) {
    if (!avisoEl) return;
    avisoEl.textContent = texto;
    avisoEl.hidden = false;
    avisoEl.classList.toggle('is-error', Boolean(esError));
    if (avisoTimer) clearTimeout(avisoTimer);
    // Se borra solo, pero el re-pintado de la lista puede sustituirlo por el
    // conteo de incompletas: los dos usan el mismo espacio a propósito, porque
    // los dos hablan del estado de la libreta.
    avisoTimer = setTimeout(function () {
      if (avisoEl.textContent === texto) { avisoEl.hidden = true; avisoEl.classList.remove('is-error'); }
    }, 5000);
  }

  // ── Carga ────────────────────────────────────────────────────────────────
  function cargar() {
    mostrar('cargando');
    N.listar().then(function (r) {
      if (r.estado !== 'ok') { mostrar('error'); return; }
      estado.direcciones = r.direcciones;
      estado.tope = r.tope;
      estado.listas = true;
      pintar();
    });
  }

  if (agregarEl) agregarEl.addEventListener('click', function () { abrirForm(null); });
  cargar();
  cargarEmpresa();
})();
